import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Clock, Users, ChevronDown, RotateCcw, ArrowRight, ArrowUpRight, Loader2, Leaf, Flame, Clapperboard, Play, X, Bookmark } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { MEDIA } from "@/lib/media";

const CATEGORIES = [
  { key: "", label: "All Recipes" },
  { key: "big_game", label: "Big Game" },
  { key: "small_game", label: "Small Game" },
  { key: "upland_bird", label: "Upland Birds" },
  { key: "waterfowl", label: "Waterfowl" },
];
const TIME_OPTIONS = [
  { label: "Under 30 min", value: 30 },
  { label: "Under 1 hr", value: 60 },
  { label: "Under 2 hrs", value: 120 },
];

function Antler({ className = "", size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M24 44V22" />
      <path d="M24 22c-2-3-2-6-5-8-2-1.5-5-1-6-3 2 .5 3-.5 4-2M24 22c2-3 2-6 5-8 2-1.5 5-1 6-3-2 .5-3-.5-4-2" />
      <path d="M19 14c-1.5-2-1.5-5-4-6-1.6-1-4-.6-4.8-2.4 1.6.4 2.4-.4 3.2-1.6M29 14c1.5-2 1.5-5 4-6 1.6-1 4-.6 4.8-2.4-1.6.4-2.4-.4-3.2-1.6" />
      <path d="M22 20c-1-2-3-2.5-4.5-4M26 20c1-2 3-2.5 4.5-4" />
    </svg>
  );
}

function Dropdown({ label, value, options, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const current = options.find((o) => o.value === value);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid={testid}
        className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border rounded-sm transition-colors ${value !== "" && value != null ? "border-blaze text-blaze bg-blaze/10" : "border-white/15 text-bone/70 bg-transparent hover:border-blaze hover:text-bone"}`}>
        {current ? current.label : label} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 min-w-[180px] bg-charcoal border border-white/15 shadow-xl py-1 rounded-sm" data-testid={`${testid}-menu`}>
          <button onClick={() => { onChange(""); setOpen(false); }} className="w-full text-left px-3.5 py-2 text-sm text-bone/50 hover:bg-white/5">Any {label.toLowerCase()}</button>
          {options.map((o) => (
            <button key={o.value} onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3.5 py-2 text-sm hover:bg-white/5 ${o.value === value ? "text-blaze font-semibold" : "text-bone/80"}`}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RecipeCard({ r, i, onOpen, saved, onToggleSave }) {
  return (
    <motion.button
      onClick={() => onOpen(r.id)}
      initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
      className="group relative h-[360px] overflow-hidden bg-charcoal flex flex-col justify-end text-left rounded-sm"
      data-testid={`recipe-card-${r.id}`}
    >
      <img src={r.image_url || MEDIA.exploreRecipes} alt={r.name} loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/92 via-black/45 to-transparent" />
      <span
        role="button" tabIndex={0}
        onClick={(e) => { e.stopPropagation(); onToggleSave(r.id); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onToggleSave(r.id); } }}
        className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full grid place-items-center backdrop-blur-sm transition-colors ${saved ? "bg-blaze text-white" : "bg-black/40 text-bone hover:bg-black/60"}`}
        aria-label={saved ? "Remove from saved" : "Save recipe"}
        data-testid={`recipe-card-save-${r.id}`}
      >
        <Bookmark size={16} className={saved ? "fill-white" : ""} />
      </span>
      <div className="relative p-5">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-blaze">{[r.game, r.cut].filter(Boolean).join(" · ")}</span>
        <h3 className="font-serif text-[26px] leading-[1.05] text-bone mt-1.5">{r.name}</h3>
        {r.blurb && <p className="text-bone/70 text-[13px] mt-1.5 line-clamp-2 leading-snug">{r.blurb}</p>}
        <div className="flex items-center gap-4 text-bone/60 text-xs mt-3">
          {r.time_minutes ? <span className="flex items-center gap-1.5"><Clock size={13} /> {r.time_minutes} min</span> : null}
          {r.servings ? <span className="flex items-center gap-1.5"><Users size={13} /> {r.servings}</span> : null}
          {r.difficulty ? <span className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-bone/50" /> {r.difficulty}</span> : null}
        </div>
      </div>
    </motion.button>
  );
}

function FilmsSection({ videos, navigate }) {
  const [active, setActive] = useState(null);
  if (!videos || videos.length === 0) return null; // never show fake films
  return (
    <section className="bg-[#0E0D0B] py-16 lg:py-20 border-t border-white/5" data-testid="films-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-blaze">Field to Table Films</span>
            <h2 className="font-serif text-4xl sm:text-5xl mt-2 text-bone">The story behind the meal.</h2>
          </div>
          <button onClick={() => navigate("/recipes")} className="text-sm font-semibold text-blaze hover:text-blaze-light inline-flex items-center gap-1.5" data-testid="films-view-all">View all films <ArrowRight size={15} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {videos.slice(0, 3).map((v) => (
            <button key={v.id} onClick={() => setActive(v)} className="group relative h-[240px] overflow-hidden rounded-sm bg-black text-center flex flex-col justify-end" data-testid={`film-card-${v.id}`}>
              <img src={v.thumbnail || MEDIA.exploreRecipes} alt={v.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/20" />
              <span className="absolute inset-0 grid place-items-center">
                <span className="w-16 h-16 rounded-full border-2 border-bone/80 grid place-items-center backdrop-blur-sm bg-black/20 group-hover:scale-110 transition-transform"><Play size={24} className="text-bone ml-1" fill="currentColor" /></span>
              </span>
              <div className="relative p-5">
                <h3 className="font-semibold uppercase tracking-wide text-bone text-sm">{v.title}</h3>
                <p className="text-bone/60 text-xs mt-1">{[v.species, v.method || v.category, v.runtime].filter(Boolean).join(" · ")}</p>
              </div>
            </button>
          ))}
        </div>
        <div className="text-center mt-10">
          <button onClick={() => navigate("/recipes")} className="border border-blaze text-blaze px-6 py-3 text-sm font-semibold hover:bg-blaze hover:text-white transition-colors inline-flex items-center gap-2" data-testid="films-more-btn">See more films and techniques <ArrowRight size={15} /></button>
        </div>
      </div>
      {active && (
        <div className="fixed inset-0 z-[70] bg-black/85 grid place-items-center p-4" onClick={() => setActive(null)} data-testid="film-modal">
          <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end"><button onClick={() => setActive(null)} className="text-bone/70 hover:text-bone mb-2" aria-label="Close" data-testid="film-modal-close"><X size={26} /></button></div>
            <div className="aspect-video bg-black">
              {active.video_url ? (
                <video src={active.video_url} controls autoPlay className="w-full h-full" data-testid="film-player" />
              ) : <div className="w-full h-full grid place-items-center text-bone/60">Video unavailable.</div>}
            </div>
            <h3 className="font-serif text-2xl text-bone mt-4">{active.title}</h3>
            {active.description && <p className="text-bone/70 text-sm mt-1">{active.description}</p>}
          </div>
        </div>
      )}
    </section>
  );
}

const TRUST = [
  { icon: Antler, title: "From the Hunter", text: "Real recipes and techniques from hunters who cook what they harvest." },
  { icon: Leaf, title: "Respect the Meat", text: "Nose-to-tail inspiration that honors the animal and reduces waste." },
  { icon: Flame, title: "Tested & Trusted", text: "Every recipe is tested in real kitchens and camps, not just written down." },
  { icon: Clapperboard, title: "Stories That Inspire", text: "Films that capture the moments between the field and the table." },
];

export default function Recipes() {
  const navigate = useNavigate();
  const { isAuthed } = useAuth();
  const { openAuth } = useUI();
  const [recipes, setRecipes] = useState(null);
  const [videos, setVideos] = useState([]);
  const [filters, setFilters] = useState({ cuts: [], methods: [], difficulties: [] });
  const [savedIds, setSavedIds] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [cut, setCut] = useState("");
  const [method, setMethod] = useState("");
  const [time, setTime] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [limit, setLimit] = useState(8);
  const [loading, setLoading] = useState(false);

  // Load videos + filter options once.
  useEffect(() => {
    api.recipeVideos().then((d) => setVideos(Array.isArray(d) ? d : [])).catch(() => setVideos([]));
    api.recipeFilters(undefined).then(setFilters).catch(() => {});
  }, []);

  // Load saved ids + hunt-based suggestions for signed-in hunters.
  const loadSaved = () => { if (isAuthed) api.savedRecipeIds().then((d) => setSavedIds(d.ids || [])).catch(() => {}); };
  useEffect(() => {
    if (!isAuthed) { setSavedIds([]); setSuggested([]); return; }
    loadSaved();
    api.recipeSuggestions().then((d) => setSuggested(d.recipes || [])).catch(() => {});
  }, [isAuthed]);

  const toggleSave = async (rid) => {
    if (!isAuthed) { openAuth("login"); return; }
    setSavedIds((ids) => ids.includes(rid) ? ids.filter((x) => x !== rid) : [...ids, rid]);
    try {
      const res = await api.saveRecipe(rid);
      setSavedIds((ids) => res.saved ? [...new Set([...ids, rid])] : ids.filter((x) => x !== rid));
      if (cat === "saved") { api.savedRecipes().then((d) => setRecipes(Array.isArray(d) ? d : [])).catch(() => {}); }
    } catch { loadSaved(); }
  };

  // Fetch recipes whenever filters change (dynamic, no reload).
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      if (cat === "saved") {
        api.savedRecipes().then((d) => setRecipes(Array.isArray(d) ? d : [])).catch(() => setRecipes([])).finally(() => setLoading(false));
        setLimit(8);
        return;
      }
      const params = {};
      if (cat) params.category = cat;
      if (q.trim()) params.q = q.trim();
      if (cut) params.cut = cut;
      if (method) params.method = method;
      if (time) params.max_minutes = time;
      if (difficulty) params.difficulty = difficulty;
      api.recipes(params)
        .then((d) => setRecipes(Array.isArray(d) ? d : []))
        .catch(() => setRecipes([]))
        .finally(() => setLoading(false));
      setLimit(8);
    }, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [cat, q, cut, method, time, difficulty]);

  const reset = () => { setCat(""); setQ(""); setCut(""); setMethod(""); setTime(""); setDifficulty(""); };
  const anyFilter = cat || q || cut || method || time || difficulty;
  const shown = (recipes || []).slice(0, limit);
  const toOpts = (arr) => (arr || []).map((v) => ({ label: v, value: v }));
  const showSuggested = isAuthed && suggested.length > 0 && !anyFilter;

  return (
    <div className="bg-[#0E0D0B] min-h-screen text-bone" data-testid="recipes-page">
      <SiteHeader />

      {/* ===== HERO ===== */}
      <section className="relative bg-[#0E0D0B] overflow-hidden" data-testid="recipes-hero">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-[1fr_1.15fr] items-stretch">
          <div className="px-5 sm:px-10 pt-10 pb-10 lg:py-24 flex flex-col justify-center relative z-10">
            <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl leading-[0.88] tracking-tight text-bone">Wild game,<br />done right.</h1>
            <p className="text-base sm:text-lg text-bone/60 mt-6 max-w-md leading-relaxed">Field-to-table recipes, techniques, and films to make the most of every harvest.</p>
            <div className="flex items-center gap-3 mt-8 text-blaze">
              <Antler size={30} className="text-blaze" />
              <span style={{ fontFamily: '"Dancing Script", cursive' }} className="text-3xl sm:text-4xl">From field to table.</span>
            </div>
          </div>
          <div className="relative min-h-[280px] lg:min-h-[540px]">
            <img src={MEDIA.exploreRecipes} alt="Wild game seared in a cast-iron skillet over an open fire" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0E0D0B] via-[#0E0D0B]/25 to-transparent hidden lg:block" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E0D0B] via-transparent to-transparent lg:hidden" />
          </div>
        </div>
      </section>

      {/* ===== SEARCH + FILTERS ===== */}
      <section className="bg-[#0E0D0B] border-b border-white/10" data-testid="recipes-filters">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-6">
          <div className="relative max-w-xl">
            <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-bone/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipes, cuts, or ingredients…"
              className="w-full bg-white/5 border border-white/15 pl-10 pr-4 py-3 text-sm text-bone placeholder:text-bone/40 outline-none focus:border-blaze transition-colors rounded-sm" data-testid="recipe-search-input" />
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {CATEGORIES.map((c) => (
              <button key={c.key} onClick={() => setCat(c.key)} data-testid={`recipe-cat-${c.key || "all"}`}
                className={`relative px-4 py-2 text-sm font-semibold border rounded-sm transition-colors ${cat === c.key ? "border-transparent text-blaze" : "border-white/15 text-bone/70 hover:border-blaze hover:text-bone"}`}>
                {c.label}
                {cat === c.key && <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-blaze rounded-full" />}
              </button>
            ))}
            {isAuthed && (
              <button onClick={() => setCat("saved")} data-testid="recipe-cat-saved"
                className={`relative px-4 py-2 text-sm font-semibold border rounded-sm inline-flex items-center gap-1.5 transition-colors ${cat === "saved" ? "border-transparent text-blaze" : "border-white/15 text-bone/70 hover:border-blaze hover:text-bone"}`}>
                <Bookmark size={14} className={cat === "saved" ? "fill-blaze" : ""} /> Saved
                {cat === "saved" && <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-blaze rounded-full" />}
              </button>
            )}
            <span className="hidden lg:block w-px h-6 bg-white/15 mx-1" />
            <div className="flex flex-wrap items-center gap-2">
              <Dropdown label="Cut" value={cut} onChange={setCut} options={toOpts(filters.cuts)} testid="recipe-filter-cut" />
              <Dropdown label="Method" value={method} onChange={setMethod} options={toOpts(filters.methods)} testid="recipe-filter-method" />
              <Dropdown label="Time" value={time} onChange={setTime} options={TIME_OPTIONS} testid="recipe-filter-time" />
              <Dropdown label="Difficulty" value={difficulty} onChange={setDifficulty} options={toOpts(filters.difficulties)} testid="recipe-filter-difficulty" />
              {anyFilter && (
                <button onClick={reset} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-bone/60 hover:text-blaze transition-colors" data-testid="recipe-reset">
                  Reset <RotateCcw size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== RECIPES GRID ===== */}
      <main className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-12 pb-16">
        {showSuggested && (
          <div className="mb-12" data-testid="recipes-suggested">
            <h2 className="text-[13px] font-bold uppercase tracking-[0.22em] text-blaze">Suggested for your hunts</h2>
            <p className="text-sm text-bone/55 mt-1">Based on the species in your hunts.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-5">
              {suggested.slice(0, 4).map((r, i) => (
                <RecipeCard key={r.id} r={r} i={i} onOpen={(id) => navigate(`/recipes/${id}`)} saved={savedIds.includes(r.id)} onToggleSave={toggleSave} />
              ))}
            </div>
          </div>
        )}

        <div className="flex items-end justify-between mb-7">
          <h2 className="text-[13px] font-bold uppercase tracking-[0.22em] text-bone/80">{cat === "saved" ? "Saved recipes" : "Recipes"}</h2>
          {cat !== "saved" && <button onClick={() => setLimit((recipes || []).length)} className="text-sm font-semibold text-blaze hover:text-blaze-light inline-flex items-center gap-1.5" data-testid="recipes-view-all">View all recipes <ArrowRight size={15} /></button>}
        </div>

        {recipes === null ? (
          <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div>
        ) : shown.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-white/15" data-testid="recipes-empty">
            {cat === "saved" ? (
              <>
                <Bookmark size={30} className="mx-auto text-bone/40" />
                <p className="font-serif text-2xl text-bone mt-4">No saved recipes yet.</p>
                <p className="text-bone/55 mt-1 text-sm">Tap the bookmark on any recipe to save it here.</p>
                <button onClick={() => setCat("")} className="text-blaze font-semibold mt-3">Browse recipes</button>
              </>
            ) : (
              <>
                <p className="font-serif text-2xl text-bone">No recipes match those filters.</p>
                <button onClick={reset} className="text-blaze font-semibold mt-3">Clear filters</button>
              </>
            )}
          </div>
        ) : (
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 transition-opacity ${loading ? "opacity-60" : "opacity-100"}`} data-testid="recipes-grid">
            {shown.map((r, i) => <RecipeCard key={r.id} r={r} i={i} onOpen={(id) => navigate(`/recipes/${id}`)} saved={savedIds.includes(r.id)} onToggleSave={toggleSave} />)}
          </div>
        )}

        {recipes && recipes.length > shown.length && (
          <div className="text-center mt-10">
            <button onClick={() => setLimit((l) => l + 8)} className="border border-blaze text-blaze px-7 py-3 text-sm font-semibold hover:bg-blaze hover:text-white transition-colors inline-flex items-center gap-2 rounded-sm" data-testid="recipes-explore-more">
              Explore more recipes <ArrowRight size={15} />
            </button>
          </div>
        )}
      </main>

      {/* ===== FILMS (hidden when no published videos) ===== */}
      <FilmsSection videos={videos} navigate={navigate} />

      {/* ===== TRUST STRIP ===== */}
      <section className="bg-[#0E0D0B] border-t border-white/10 py-14" data-testid="recipes-trust">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-0">
          {TRUST.map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={t.title} className={`flex flex-col items-center text-center px-6 ${i > 0 ? "lg:border-l lg:border-white/10" : ""}`}>
                <span className="w-14 h-14 rounded-full border border-blaze/40 grid place-items-center text-blaze">
                  <Icon size={26} className="text-blaze" />
                </span>
                <h3 className="text-[12px] font-bold uppercase tracking-[0.18em] text-bone mt-4">{t.title}</h3>
                <p className="text-sm text-bone/55 mt-2 leading-relaxed max-w-[220px]">{t.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}
